import { ReactNode, useEffect, useMemo, useState } from "react";
import { ScoreType, useGetAllScoreType } from "src/api/client-api/scoreType/useGetAllScoreType";
import { useGetAllScore } from "src/api/sure-api/score/useGetAllScore";
import { getPreferenceFromCache, setPreference } from "src/services/PreferenceStore";
import { usePreferencesSync } from "src/services/usePreferencesSync";
import {
    CAMPAIGN,
    ACTION_PLAN_TEMPLATE,
    ORGANIZATION,
    COMPANY_SECTOR,
    CREATE,
    CHANGE,
    CUSTOM_THEME,
    DELETE,
    FoodpilotContext,
    FoodpilotContextInitialValues,
    INGREDIENT,
    MODIFY,
    PACKAGING,
    PRODUCT,
    PRODUCT_LINE,
    SCORE_TYPE,
    STRATEGY,
    BRAND,
    INGREDIENT_LINE,
    PRODUCT_ACTIVITY_DATA,
    PUCK_EXTENSION,
    USER,
    VIEW,
    ANSWER_SSQ,
    ASSIGN,
    IMPERSONATE,
    AUDIT_LOG,
    DASHBOARD,
    SETTINGS,
    VIEW_ORG_SETTINGS,
    MODIFY_ORG_SETTINGS,
    VIEW_OVERVIEW,
    VIEW_SSQ,
    VALIDATE_SSQ,
    VIEW_NETWORK_TRACKING,
    JsonAttributesByEntity,
    ROLE,
    SUPPLIER,
} from "./FoodpilotContext";
import { Campaign, useGetAllCampaigns } from "src/api/client-api/useCampaign";
import { useSelectCompany } from "./useSelectCompany";
import { Score } from "src/types";

import { useGetConsentGateItems } from "src/api/client-api/consent/useGetConsentGateItems";
import { useGetConsentPreferences } from "src/api/client-api/consent/useGetConsentPreferences";
import { CONSENT_GOVERNANCE_FEATURE_SWITCH } from "src/api/client-api/consent/constants";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { useUserContext } from "./User/useUserContext";
import { Loading } from "src/components/utils/Loading";
import { useGetAllJsonAttributes } from "src/api/client-api/jsonAttributes/useJsonAttribute";
import { useLocation, useNavigate } from "react-router";
import {
    buildOrganizationPermissions,
    getSelectableCompanies,
    hasOrganizationOrPlatformScopedPermission,
} from "./permissionMatching";
import { requiresCompanyContext } from "./companyContextRouting";
import { replaceCampaignIdInPath, resolveCampaignFromPath } from "./campaignRouting";

const currentCampaignKey = "currentCampaignId";

type ProtectedContextProviderProps = {
    children: ReactNode;
};

const SLUGS = [
    VIEW,
    MODIFY,
    DELETE,
    CREATE,
    CHANGE,
    VIEW_OVERVIEW,
    VIEW_ORG_SETTINGS,
    MODIFY_ORG_SETTINGS,
    ANSWER_SSQ,
    VIEW_SSQ,
    VALIDATE_SSQ,
    ASSIGN,
    IMPERSONATE,
    VIEW_NETWORK_TRACKING,
] as const;
export type Slugs = (typeof SLUGS)[number];

const ENTITIES = [
    PRODUCT,
    INGREDIENT,
    PACKAGING,
    ORGANIZATION,
    SUPPLIER,
    COMPANY_SECTOR,
    USER,
    ROLE,
    STRATEGY,
    PRODUCT_LINE,
    SCORE_TYPE,
    CAMPAIGN,
    ACTION_PLAN_TEMPLATE,
    BRAND,
    INGREDIENT_LINE,
    PRODUCT_ACTIVITY_DATA,
    PUCK_EXTENSION,
    SETTINGS,
    DASHBOARD,
    AUDIT_LOG,
    CUSTOM_THEME,
];
export type Entities = (typeof ENTITIES)[number];

export const ProtectedContextProvider = (props: ProtectedContextProviderProps) => {
    const { children } = props;

    const { user } = useUserContext();
    const location = useLocation();
    const navigate = useNavigate();
    const { isSynced: preferencesSynced } = usePreferencesSync();
    const { isFeatureActivated } = useApplicationParameters();
    const isConsentGovernanceEnabled = isFeatureActivated(CONSENT_GOVERNANCE_FEATURE_SWITCH);

    const companies = user === undefined ? null : getSelectableCompanies(user);
    const [currentCampaign, _setCurrentCampaign] = useState<Campaign | null>(null);
    const [campaignInitialized, setCampaignInitialized] = useState(false);
    const [jsonAttributesByEntity, setJsonAttributesByEntity] = useState<JsonAttributesByEntity | undefined>();

    const { selectedCompany, setSelectedCompany } = useSelectCompany(companies);
    const {
        scoreTypes,
        error: scoreTypesError,
        isLoading: scoreTypesLoading,
        refetch: retryScoreTypes,
        status: scoreTypesStatus,
    } = useGetAllScoreType();
    const { scores, isLoading: scoresLoading } = useGetAllScore();
    const {
        campaigns,
        isLoading: campaignsLoading,
        error: campaignsError,
        isError: isCampaignsError,
        refetch: retryCampaigns,
        status: campaignsStatus,
    } = useGetAllCampaigns({ orderByDate: true });
    const { consentGateItems, isLoading: isConsentGateItemsLoading } = useGetConsentGateItems({
        enabled: isConsentGovernanceEnabled,
    });
    const { consentPreferences, isLoading: isConsentPreferencesLoading } = useGetConsentPreferences({
        enabled: isConsentGovernanceEnabled,
    });
    const pendingConsentPreferences = useMemo(
        () => (consentPreferences ?? []).filter((preference) => preference.updatedAt == null),
        [consentPreferences],
    );
    const { jsonAttributes, isLoading: isJsonAttributesLoading } = useGetAllJsonAttributes();

    // The first one is good enough for our use-case;
    // We're not supposed to have multiple ones;
    const targetCampaign = campaigns.find((c) => c.isTarget === true);

    const setCurrentCampaign = (campaign: Campaign | null) => {
        _setCurrentCampaign(campaign);

        if (campaign !== null) {
            localStorage.setItem(currentCampaignKey, campaign.id.toString());
            setPreference("ui", "currentCampaignId", campaign.id.toString());
        } else {
            localStorage.removeItem(currentCampaignKey);
        }

        if (campaign === null) {
            return;
        }

        const newPathname = replaceCampaignIdInPath(location.pathname, campaign.id);
        if (newPathname === location.pathname) {
            return;
        }

        navigate({ pathname: newPathname, search: location.search, hash: location.hash });
    };

    const canUser = (slug: Slugs, entity: Entities) => {
        if (user === undefined) {
            return false;
        }

        const userRoles = user.roles;

        if (userRoles.includes("ROLE_ADMIN")) {
            return true;
        }

        return hasOrganizationOrPlatformScopedPermission(
            buildOrganizationPermissions(user.organizations),
            selectedCompany?.id ?? null,
            slug,
            entity,
            user.permissions,
        );
    };

    const isAdmin = () => {
        if (user === undefined) {
            return false;
        }

        const userRoles = user.roles;

        return userRoles.includes("ROLE_ADMIN");
    };

    const isUserOfTypeSupplier = (): boolean => {
        return selectedCompany?.isProvider ?? false;
    };

    const getScoresByType = (type: ScoreType["type"]): Score[] => {
        const scoreType = scoreTypes?.find((scoreType: ScoreType) => scoreType.type === type);
        const scoreIds = scoreType?.scoreIds ?? [];

        if (!scoreType || !scores) return [];

        // Respecter l'ordre exact de scoreIds (pas de tri automatique)
        return scores
            .filter((score: Score) => {
                return scoreIds.indexOf(score.id) > -1;
            })
            .sort((a, b) => scoreIds.indexOf(a.id) - scoreIds.indexOf(b.id));
    };

    useEffect(() => {
        if (!preferencesSynced) return;

        // Wait for campaigns to be loaded (success or error)
        if (campaignsLoading) return;

        // If there's an error loading campaigns, still initialize to prevent blocking
        if (isCampaignsError) {
            setCampaignInitialized(true);
            return;
        }

        // If no campaigns available, mark as initialized
        if (campaigns === undefined || campaigns.length === 0) {
            setCampaignInitialized(true);
            return;
        }

        const cachedCampaignId = getPreferenceFromCache<string>("ui", "currentCampaignId");
        const storedCampaignRaw = cachedCampaignId ?? localStorage.getItem(currentCampaignKey);
        const storedCampaignId = storedCampaignRaw ? parseInt(storedCampaignRaw) : null;

        const { campaign, shouldReplacePath } = resolveCampaignFromPath(campaigns, location.pathname, storedCampaignId);

        _setCurrentCampaign(campaign);

        if (campaign !== null) {
            localStorage.setItem(currentCampaignKey, campaign.id.toString());
            setPreference("ui", "currentCampaignId", campaign.id.toString());
        }

        if (campaign !== null && shouldReplacePath) {
            navigate(
                {
                    pathname: replaceCampaignIdInPath(location.pathname, campaign.id),
                    search: location.search,
                    hash: location.hash,
                },
                { replace: true },
            );
        }

        setCampaignInitialized(true);
    }, [
        campaigns,
        campaignsLoading,
        isCampaignsError,
        location.hash,
        location.pathname,
        location.search,
        navigate,
        preferencesSynced,
    ]);

    useEffect(() => {
        if (!jsonAttributes || jsonAttributes.length === 0) {
            // Reset to empty if no attributes
            setJsonAttributesByEntity((prev) => (prev ? {} : prev));
            return;
        }

        const structuredData = jsonAttributes.reduce((acc, attr) => {
            const bucket = (acc[attr.entityClass] ??= []);
            bucket.push(attr);
            return acc;
        }, {} as JsonAttributesByEntity);

        setJsonAttributesByEntity(structuredData);
    }, [jsonAttributes]);

    // Wait for all critical data to be ready before rendering children
    const isCompanyContextRequired = requiresCompanyContext(location.pathname);
    if (
        user === undefined ||
        !preferencesSynced ||
        !campaignInitialized ||
        isConsentGateItemsLoading ||
        isConsentPreferencesLoading ||
        isJsonAttributesLoading ||
        (isCompanyContextRequired && selectedCompany === null)
    ) {
        return <Loading />;
    }

    const selectableCompanies = companies ?? [];

    const items: FoodpilotContextInitialValues = {
        user: { trustedUser: user },
        pendingConsentItems: consentGateItems,
        pendingConsentPreferences,
        canUser,
        isAdmin,
        scoreTypes,
        scoreTypesError,
        scoreTypesLoading,
        scoreTypesStatus,
        retryScoreTypes,
        scores,
        scoresLoading,
        campaigns,
        campaignsError,
        campaignsStatus,
        retryCampaigns,
        targetCampaign,
        currentCampaign,
        setCurrentCampaign,
        userCompanies: selectableCompanies,
        selectedCompany,
        setSelectedCompany,
        isUserOfTypeSupplier,
        getScoresByType,
        entitiesJsonAttributes: jsonAttributesByEntity,
    };

    return <FoodpilotContext.Provider value={items}>{children}</FoodpilotContext.Provider>;
};
